import axios from "axios";
import { Track, DjSet, AnalysisStatusResponse, ImportPlaylistResponse } from "../types";

/**
 * Resolve API base URL (backend origin only, no `/api` suffix).
 * On Vercel, relative "/api" hits the static host → 405 on POST; use absolute Railway URL in prod.
 * Paths below always start with `/api/...` so we never depend on baseURL containing `/api`
 * (Vercel often sets REACT_APP_API_BASE_URL to the origin only).
 */
const PRODUCTION_API_FALLBACK = "https://fabulous-youthfulness-production-4b95.up.railway.app";

function normalizeBackendOrigin(url: string): string {
  const b = url.replace(/\/$/, "");
  if (b.endsWith("/api")) return b.slice(0, -4);
  return b;
}

function resolveApiBaseUrl(): string {
  const raw = process.env.REACT_APP_API_BASE_URL?.trim();
  if (raw && /^https?:\/\//i.test(raw)) {
    return normalizeBackendOrigin(raw);
  }
  if (process.env.NODE_ENV === "production") {
    return normalizeBackendOrigin(PRODUCTION_API_FALLBACK);
  }
  return "";
}

const baseURL = resolveApiBaseUrl();

/**
 * Canonical public site for marketing pages, share links, OG URLs (no trailing slash).
 * Set `REACT_APP_PUBLIC_SITE_URL` in Vercel / `.env.production` (e.g. https://setpilot.org).
 */
export function getPublicSiteOrigin(): string {
  const u = process.env.REACT_APP_PUBLIC_SITE_URL?.trim();
  if (u && /^https?:\/\//i.test(u)) return u.replace(/\/$/, "");
  if (typeof window !== "undefined" && window.location?.origin) {
    return window.location.origin.replace(/\/$/, "");
  }
  return "";
}

/** Shown on /contact and in mailto links. Set `REACT_APP_CONTACT_EMAIL` in Vercel if different. */
export function getContactEmail(): string {
  const e = process.env.REACT_APP_CONTACT_EMAIL?.trim();
  if (e && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) return e;
  return "hello@setpilot.org";
}

const api = axios.create({
  baseURL,
});

/** Spring: TrackController @RequestMapping("/api/tracks") — always use full path from app root. */
export const uploadTrack = async (
  file: File,
  sessionId: string,
  onProgress?: (pct: number) => void,
  signal?: AbortSignal
): Promise<Track> => {
  const form = new FormData();
  form.append("file", file);
  form.append("sessionId", sessionId);
  const { data } = await api.post<Track>("/api/tracks/upload", form, {
    headers: { "Content-Type": "multipart/form-data" },
    signal,
    onUploadProgress: (e) => {
      if (onProgress && e.total) onProgress(Math.round((e.loaded / e.total) * 100));
    },
  });
  return data;
};

export const getTracks = async (sessionId: string): Promise<Track[]> => {
  const { data } = await api.get<Track[]>(`/api/tracks?sessionId=${encodeURIComponent(sessionId)}`);
  return data;
};

export const importPlaylist = async (
  sessionId: string,
  playlistUrl: string,
  providerAccessToken?: string,
  oauthSessionId?: string
): Promise<ImportPlaylistResponse> => {
  const { data } = await api.post<ImportPlaylistResponse>("/api/tracks/import-playlist", {
    sessionId,
    playlistUrl,
    providerAccessToken: providerAccessToken?.trim() || undefined,
    oauthSessionId: oauthSessionId?.trim() || undefined,
  });
  return data;
};

export interface PlaylistOAuthStartResponse {
  provider: string;
  authUrl: string;
}

export interface PlaylistOAuthCallbackResponse {
  provider: string;
  sessionId: string;
  oauthSessionId: string;
  expiresAtEpochSeconds: number;
}

export const startPlaylistOAuth = async (
  provider: "spotify" | "youtube",
  sessionId: string
): Promise<PlaylistOAuthStartResponse> => {
  const { data } = await api.get<PlaylistOAuthStartResponse>(
    `/api/auth/playlists/${provider}/start?sessionId=${encodeURIComponent(sessionId)}`
  );
  return data;
};

export const completePlaylistOAuth = async (
  provider: "spotify" | "youtube",
  code: string,
  state: string
): Promise<PlaylistOAuthCallbackResponse> => {
  const { data } = await api.get<PlaylistOAuthCallbackResponse>(
    `/api/auth/playlists/${provider}/callback?code=${encodeURIComponent(code)}&state=${encodeURIComponent(state)}`
  );
  return data;
};

export const getAnalysisStatus = async (sessionId: string): Promise<AnalysisStatusResponse> => {
  const { data } = await api.get<AnalysisStatusResponse>(
    `/api/tracks/status?sessionId=${encodeURIComponent(sessionId)}`
  );
  return data;
};

/** Normalize Spring JSON (snake/camel) into our DjSet shape. */
export function normalizeDjSet(data: any): DjSet {
  return {
    id: data.id,
    sessionId: data.sessionId ?? undefined,
    status: data.status,
    orderedTracks: data.orderedTracks ?? [],
    transitions: data.transitions ?? [],
    totalDurationSeconds: data.totalDurationSeconds ?? null,
    createdAt: data.createdAt,
    completedAt: data.completedAt ?? null,
    shareToken: data.shareToken ?? null,
    profile: data.profile ?? null,
  };
}

export const generateSet = async (
  sessionId: string,
  trackIds?: number[],
  profile?: "SMOOTH" | "ENERGETIC" | "WILD"
): Promise<DjSet> => {
  const body: any = {};
  if (trackIds && trackIds.length > 0) body.trackIds = trackIds;
  if (profile) body.profile = profile;
  const payload = Object.keys(body).length > 0 ? body : undefined;
  const { data } = await api.post<any>(
    `/api/sets/generate?sessionId=${encodeURIComponent(sessionId)}`,
    payload
  );
  return normalizeDjSet(data);
};

/** Persist share token and get path like /set/abc123 (prepend origin for full URL). */
export const saveSet = async (
  setId: number,
  sessionId: string
): Promise<{ shareToken: string; path: string }> => {
  const { data } = await api.post<{ shareToken: string; path: string }>("/api/save-set", {
    setId,
    sessionId,
  });
  return data;
};

/** Public read-only set by share token (no session). */
export const getSharedSet = async (token: string): Promise<DjSet> => {
  const { data } = await api.get<any>(`/api/public/sets/${encodeURIComponent(token)}`);
  return normalizeDjSet(data);
};

export const getLatestSet = async (sessionId: string): Promise<DjSet | null> => {
  try {
    const { data } = await api.get<DjSet>(
      `/api/sets/latest?sessionId=${encodeURIComponent(sessionId)}`
    );
    return data;
  } catch {
    return null;
  }
};

export const deleteTrack = async (trackId: number): Promise<void> => {
  await api.delete(`/api/tracks/${trackId}`);
};

export const deleteAllTracks = async (sessionId: string): Promise<void> => {
  await api.delete(`/api/tracks?sessionId=${encodeURIComponent(sessionId)}`);
};

/** Same origin as axios `api` — for keepalive fetch when the page unloads. */
export function getBackendOrigin(): string {
  return baseURL;
}

/**
 * Absolute URL for backend-rendered Open Graph image (PNG) for a shared set.
 * Social crawlers fetch this directly; must be reachable from the public internet in production.
 */
export function getSharedSetOgImageUrl(token: string): string {
  const path = `/api/public/og/set/${encodeURIComponent(token)}`;
  const backend = getBackendOrigin();
  if (backend) {
    return `${backend.replace(/\/$/, "")}${path}`;
  }
  if (typeof window !== "undefined" && window.location?.origin) {
    return `${window.location.origin}${path}`;
  }
  return path;
}

/** Optional signup after set generation — not required to use the app. */
export const submitEmailCapture = async (payload: {
  email: string;
  sessionId?: string;
  setId?: number;
}): Promise<void> => {
  await api.post("/api/email-capture", {
    email: payload.email.trim(),
    sessionId: payload.sessionId,
    setId: payload.setId,
  });
};

/**
 * Best-effort cleanup when the user closes the tab (pagehide).
 * Uses fetch + keepalive so the request may complete after navigation.
 * Does not replace server-side TTL; see setpilot.session.retention-hours.
 */
export function deleteAllTracksKeepalive(sessionId: string): void {
  const origin = baseURL;
  const url = `${origin}/api/tracks?sessionId=${encodeURIComponent(sessionId)}`;
  try {
    void fetch(url, { method: "DELETE", keepalive: true, mode: "cors", credentials: "omit" });
  } catch {
    /* ignore */
  }
}

// ── Feedback (Phase 2) ────────────────────────────────────────────────────────

export interface SubmitFeedbackPayload {
  sessionId: string;
  trackIdA: number;
  trackIdB: number;
  rating?: number; // 1–5
  thumbRating?: "UP" | "DOWN";
  reordered?: boolean;
  setId?: number;
  profile?: string;
}

export const submitFeedback = async (payload: SubmitFeedbackPayload): Promise<void> => {
  await api.post("/api/feedback", payload);
};

export const logReorder = async (
  sessionId: string,
  setId: number,
  fromOrder: number[],
  toOrder: number[]
): Promise<void> => {
  await api.post("/api/sets/reorder-log", {
    sessionId,
    setId,
    fromOrder,
    toOrder,
  });
};
